import axiosClientCached from '@lib/axiosClientCached';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore';

// ── Types matching backend CompanyCourierAdvanceResource ────────────────────

export interface PaymentTerm {
  id: number;
  slug: string;
  name: string;
  days: number;
}

export interface CompanyCourierAdvance {
  id: number;
  uuid: string;
  company_id: number;
  enabled: boolean;
  credit_limit_minor: number | null;
  credit_reset_day_of_month: number | null;
  expiry_date: string | null;
  current_period_used_credit_minor?: number;
  current_available_credit_minor?: number;
  currency?: { id: number; code: string; symbol?: string };
  refreshIntervalType?: { id: number; slug: string; name: string };
  paymentTerms?: PaymentTerm[];
  created_at: string;
  updated_at: string;
}

interface CourierAdvanceState {
  facility: CompanyCourierAdvance | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: CourierAdvanceState = {
  facility: null,
  loading: false,
  saving: false,
  error: null,
};

// ── Thunks ──────────────────────────────────────────────────────────────────

export const fetchCourierAdvanceFacility = createAsyncThunk<
  CompanyCourierAdvance | null,
  { companySlug: string },
  { rejectValue: string }
>('courierAdvance/fetchFacility', async ({ companySlug }, { rejectWithValue }) => {
  try {
    const res = await axiosClientCached.get<{ data: CompanyCourierAdvance } | CompanyCourierAdvance>(
      `/c/${companySlug}/admin/billing/company-courier-advance`,
      { cache: false },
    );
    // API resource shape is { data: {...} }
    const body = res.data as { data?: CompanyCourierAdvance };
    return body.data ?? (res.data as CompanyCourierAdvance);
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
    if (err.response?.status === 404) return null; // No facility exists yet — normal state
    return rejectWithValue(err.response?.data?.message ?? err.message ?? 'Failed to fetch courier advance facility');
  }
});

export interface ApplyForCourierAdvancePayload {
  company_slug: string;
  credit_limit_minor?: number | null;
  currency_slug?: string;
  refresh_interval_slug?: string;
  credit_reset_day_of_month?: number | null;
  expiry_date?: string | null;
  payment_terms?: string[];
  enabled?: boolean;
}

export const saveCourierAdvanceFacility = createAsyncThunk<
  CompanyCourierAdvance,
  ApplyForCourierAdvancePayload,
  { rejectValue: string }
>('courierAdvance/saveFacility', async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosClientCached.post<{ data: CompanyCourierAdvance }>(
      '/billing/create-or-update-company-courier-advance',
      payload,
      { cache: false },
    );
    return res.data.data;
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    return rejectWithValue(err.response?.data?.message ?? err.message ?? 'Failed to save courier advance facility');
  }
});

// ── Slice ───────────────────────────────────────────────────────────────────

const courierAdvanceSlice = createSlice({
  name: 'courierAdvance',
  initialState,
  reducers: {
    clearCourierAdvanceState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourierAdvanceFacility.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchCourierAdvanceFacility.fulfilled, (s, a: PayloadAction<CompanyCourierAdvance | null>) => {
        s.loading = false;
        s.facility = a.payload;
      })
      .addCase(fetchCourierAdvanceFacility.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload ?? 'Unknown error';
      })
      .addCase(saveCourierAdvanceFacility.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(saveCourierAdvanceFacility.fulfilled, (s, a: PayloadAction<CompanyCourierAdvance>) => {
        s.saving = false;
        s.facility = a.payload;
      })
      .addCase(saveCourierAdvanceFacility.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload ?? 'Unknown error';
      });
  },
});

export const { clearCourierAdvanceState } = courierAdvanceSlice.actions;

// ── Selectors ───────────────────────────────────────────────────────────────

export const selectCourierAdvanceFacility = (s: AppState) => s.courierAdvance.facility;
export const selectCourierAdvanceLoading = (s: AppState) => s.courierAdvance.loading;
export const selectCourierAdvanceSaving = (s: AppState) => s.courierAdvance.saving;
export const selectCourierAdvanceError = (s: AppState) => s.courierAdvance.error;

// Convenience selectors for common display fields
export const selectCreditLimitMinor = (s: AppState) => s.courierAdvance.facility?.credit_limit_minor ?? 0;
export const selectAvailableCreditMinor = (s: AppState) =>
  s.courierAdvance.facility?.current_available_credit_minor ?? 0;
export const selectUsedCreditMinor = (s: AppState) => s.courierAdvance.facility?.current_period_used_credit_minor ?? 0;
export const selectApprovedPaymentTerms = (s: AppState) => s.courierAdvance.facility?.paymentTerms ?? [];

export default courierAdvanceSlice.reducer;
